
<template>
  <form @submit.prevent="handleSubmit">
    <!-- Avatar -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center gap-3">
        <div
          class="avatar-container position-relative cursor-pointer me-2"
          style="
            width: 100px;
            height: 100px;
            border: 2px dashed #ccc;
            border-radius: 50%;
          "
          @click="$refs.fileInput.click()"
        >
          <img
            :src="avatarPreview"
            class="avatar-preview w-100 h-100"
            :alt="$t('users.form.avatarAlt')"
            @error="handleImageError"
            style="object-fit: cover; border-radius: 50%"
          />
          <input
            type="file"
            ref="fileInput"
            accept="image/*"
            class="d-none"
            @change="handleFileChange"
          />
          <button
            v-if="avatarPreview || formData.avatar_url"
            type="button"
            class="btn btn-sm btn-danger position-absolute top-0"
            :class="locale === 'ar' ? 'end-0 me-2' : 'start-0 ms-2'"
            @click.stop="removeAvatar"
            style="transform: translate(30%, -30%)"
          >
            <i class="fas fa-trash" />
          </button>
          <div
            v-if="!(avatarPreview || formData.avatar_url)"
            class="position-absolute top-50 start-50 translate-middle text-muted"
          >
            <i class="fas fa-plus" />
          </div>
        </div>
      </div>
      <div v-if="errors.avatar" class="invalid-feedback d-block">
        {{ errors.avatar[0] }}
      </div>
    </div>

    <!-- Fields -->
    <div class="row mx-2">
      <!-- Name -->
      <div class="col-md-6">
        <label class="bmd-label-float">{{ $t("users.form.name") }}</label>
        <input
          v-model="formData.name"
          type="text"
          class="form-control borderless-input"
          :class="{ 'is-invalid': errors.name }"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <div v-if="errors.name" class="invalid-feedback">
          {{ errors.name[0] }}
        </div>
      </div>

      <!-- Email -->
      <div class="col-md-6">
        <label class="bmd-label-float">{{ $t("users.form.email") }}</label>
        <input
          v-model="formData.email"
          type="email"
          class="form-control borderless-input"
          :class="{ 'is-invalid': errors.email }"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <div v-if="errors.email" class="invalid-feedback">
          {{ errors.email[0] }}
        </div>
      </div>

      <!-- Password (only add) -->
      <div v-if="!isEdit" class="col-md-6">
        <label class="bmd-label-float">{{ $t("users.form.password") }}</label>
        <input
          v-model="formData.password"
          type="password"
          class="form-control borderless-input"
          :class="{ 'is-invalid': errors.password }"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <div v-if="errors.password" class="invalid-feedback">
          {{ errors.password[0] }}
        </div>
      </div>

      <!-- Confirm password (only add) -->
      <div v-if="!isEdit" class="col-md-6">
        <label class="bmd-label-float">{{
          $t("users.form.confirmPassword")
        }}</label>
        <input
          v-model="formData.password_confirmation"
          type="password"
          class="form-control borderless-input"
          :class="{ 'is-invalid': errors.password_confirmation }"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <div v-if="errors.password_confirmation" class="invalid-feedback">
          {{ errors.password_confirmation[0] }}
        </div>
      </div>

      <!-- Gender -->
      <div class="col-md-6">
        <label class="bmd-label-float">{{ $t("users.form.gender") }}</label>
        <select
          v-model="formData.gender"
          class="form-select borderless-input"
          :class="[
            locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr',
            { 'is-invalid': errors.gender },
          ]"
        >
          <option value="male">{{ $t("users.form.male") }}</option>
          <option value="female">{{ $t("users.form.female") }}</option>
        </select>
        <div v-if="errors.gender" class="invalid-feedback">
          {{ errors.gender[0] }}
        </div>
      </div>

      <!-- Phone -->
      <div class="col-md-6">
        <label class="bmd-label-float">{{ $t("users.form.phone") }}</label>
        <input
          v-model="formData.phone_num"
          type="tel"
          class="form-control borderless-input"
          :class="{ 'is-invalid': errors.phone_num }"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <div v-if="errors.phone_num" class="invalid-feedback">
          {{ errors.phone_num[0] }}
        </div>
      </div>
<!-- Salary -->
<div class="col-md-6">
  <label class="bmd-label-float">{{ $t('users.form.salary') }}</label>
  <input
    v-model.number="formData.salary"
    type="number"
    step="1"
    min="0"
    class="form-control borderless-input"
    :class="{ 'is-invalid': errors.salary }"
    :dir="locale === 'ar' ? 'rtl' : 'ltr'"
  />
  <div v-if="errors.salary" class="invalid-feedback">
    {{ errors.salary[0] }}
  </div>
</div>

      <div class="col-12">
        <label class="bmd-label-float">{{ $t("users.form.role") }}</label>
        <select
          v-model="formData.role_id"
          class="form-select borderless-input"
          :class="[
            locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr',
            { 'is-invalid': errors.role_id },
          ]"
        >
          <option value="" disabled>{{ $t("users.form.selectRole") }}</option>
          <option
            v-for="role in translatedRoles"
            :key="role.id"
            :value="role.id"
          >
            {{ role.translated_name }}
          </option>
        </select>
        <div v-if="errors.role_id" class="invalid-feedback">
          {{ errors.role_id[0] }}
        </div>
      </div>
      <!-- School Admin notice -->
      <div v-if="isSchoolAdmin" class="col-12 mt-2">
        <div
          class="alert alert-info py-2 px-3 d-inline-flex align-items-center gap-2 admin-perms-note"
        >
          <i class="fas fa-shield-alt"></i>
          <small class="m-0">
            {{
              tt(
                "users.form.schoolAdminAllPerms",
                "All permissions are granted for School Admin. The permissions list is hidden and won’t be sent."
              )
            }}
          </small>
        </div>
      </div>

      <!-- ===== Permissions (modules based on selected role) ===== -->
      <div class="col-12 mt-3" v-if="selectedRole && !isSchoolAdmin">
        <div
          class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2"
        >
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <label class="bmd-label-float mb-0">
              {{ $t("users.form.permissions") }}
              <small class="text-muted ms-2"
                >({{ filteredModuleKeys.length }})</small
              >
            </label>

            <input
              type="text"
              class="form-control form-control-sm ms-2 perms-search"
              v-model.trim="search"
              :placeholder="tt('permissions.search', 'Search modules…')"
              :dir="locale === 'ar' ? 'rtl' : 'ltr'"
            />
          </div>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="allowAllVisible()"
            >
              {{ $t("common.selectAll") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="denyAllVisible()"
            >
              {{ $t("common.clearAll") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="toggleAllOpen"
            >
              {{
                allOpen
                  ? $t("permissions.collapseAll")
                  : $t("permissions.expandAll")
              }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="permsLoading" class="text-center py-3">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ $t("loading.message") }}</span>
          </div>
        </div>

        <!-- Empty -->
        <div
          v-else-if="filteredModuleKeys.length === 0"
          class="text-center text-muted py-3"
        >
          {{ $t("permissions.none") }}
        </div>

        <!-- Accordion list -->
        <div v-else class="accordion" id="permsAccordion">
          <div
            v-for="mod in filteredModuleKeys"
            :key="mod"
            class="accordion-item mb-2 shadow-sm border-0 rounded"
          >
            <h2 class="accordion-header">
              <button
                class="accordion-button d-flex align-items-center gap-2 perms-head-btn"
                type="button"
                :class="{ collapsed: !openModules.has(mod) }"
                @click="toggleModule(mod)"
              >
                <!-- left side: icon + title -->
                <span class="mod-left d-flex align-items-center gap-2">
                  <i class="fas fa-cube" />
                  <span class="text-truncate">{{
                    tt(`sidebar.${mod}`, mod)
                  }}</span>
                </span>

                <!-- right edge: switch + label + caret (always together) -->
                <span class="mod-right d-flex align-items-center gap-2">
                  <span class="form-check form-switch m-0" @click.stop>
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="rowAllChecked(mod)"
                      @change.stop="onToggleRow(mod, $event.target.checked)"
                      :disabled="(actionsByModule.get(mod)?.size || 0) === 0"
                    />
                  </span>
                  <small class="text-muted">{{ $t("permissions.all") }}</small>

                  <!-- custom caret -->
                  <i
                    class="accordion-caret fas"
                    :class="
                      openModules.has(mod) ? 'fa-angle-up' : 'fa-angle-down'
                    "
                    aria-hidden="true"
                  />
                </span>
              </button>
            </h2>

            <div
              :class="[
                'accordion-collapse collapse',
                { show: openModules.has(mod) },
              ]"
            >
              <div class="accordion-body py-3 bg-white">
                <div class="row gy-3">
                  <!-- DISPLAY -->
                  <div
                    v-if="actionExists(mod, 'display')"
                    class="col-6 col-md-3"
                  >
                    <small class="text-muted d-block">{{
                      $t("permissions.display")
                    }}</small>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        :checked="isAllowed(mod, 'display')"
                        @change="
                          onToggle(slug(mod, 'display'), $event.target.checked)
                        "
                      />
                      <span
                        :class="badgeClass(isAllowed(mod, 'display'), true)"
                      >
                        {{ badgeText(isAllowed(mod, "display"), true) }}
                      </span>
                    </div>
                  </div>

                  <!-- CREATE -->
                  <div
                    v-if="
                      actionExists(mod, 'create') && !isDenied(mod, 'create')
                    "
                    class="col-6 col-md-3"
                  >
                    <small class="text-muted d-block">{{
                      $t("permissions.add")
                    }}</small>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        :checked="isAllowed(mod, 'create')"
                        @change="
                          onToggle(slug(mod, 'create'), $event.target.checked)
                        "
                      />
                      <span :class="badgeClass(isAllowed(mod, 'create'), true)">
                        {{ badgeText(isAllowed(mod, "create"), true) }}
                      </span>
                    </div>
                  </div>

                  <!-- UPDATE -->
                  <div
                    v-if="
                      actionExists(mod, 'update') && !isDenied(mod, 'update')
                    "
                    class="col-6 col-md-3"
                  >
                    <small class="text-muted d-block">{{
                      $t("permissions.edit")
                    }}</small>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        :checked="isAllowed(mod, 'update')"
                        @change="
                          onToggle(slug(mod, 'update'), $event.target.checked)
                        "
                      />
                      <span :class="badgeClass(isAllowed(mod, 'update'), true)">
                        {{ badgeText(isAllowed(mod, "update"), true) }}
                      </span>
                    </div>
                  </div>

                  <!-- DELETE -->
                  <div
                    v-if="
                      actionExists(mod, 'delete') && !isDenied(mod, 'delete')
                    "
                    class="col-6 col-md-3"
                  >
                    <small class="text-muted d-block">{{
                      $t("permissions.delete")
                    }}</small>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        :checked="isAllowed(mod, 'delete')"
                        @change="
                          onToggle(slug(mod, 'delete'), $event.target.checked)
                        "
                      />
                      <span :class="badgeClass(isAllowed(mod, 'delete'), true)">
                        {{ badgeText(isAllowed(mod, "delete"), true) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Selected count for this module -->
                <div class="mt-3 d-flex align-items-center justify-content-end">
                  <small class="text-muted">
                    {{ $t("permissions.selectedInModule") }}:
                    {{ selectedCountInModule(mod) }} /
                    {{ actionsByModule.get(mod)?.size || 0 }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Save user -->

      
      </div>
      <!-- ===== /Permissions ===== -->
    </div>
  </form>
</template>
<script setup>
import {
  ref,
  watch,
  defineProps,
  defineEmits,
  defineExpose,
  onMounted,
  computed,
} from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";

const { locale, t } = useI18n();
const toast = useToast();
const store = useStore();

const isSchoolAdmin = computed(
  () => (selectedRoleSlug.value || "").trim().toLowerCase() === "school_admin"
);

const userChangedRole = ref(false);
const roleIdInitialized = ref(false);

/** ✅ guards to prevent edit permissions being overwritten */
const suppressPermsReload = ref(false);
const didLoadUserPerms = ref(false);

/** hide these roles from the selector */
const HIDDEN_ROLE_SLUGS = new Set(["student", "teacher", "guardian"]);

const ROLE_SIDEBAR_MODULES = {
  school_admin: new Set([
    "admin_dashboard",
    "users",
    "classes",
    "subjects",
    "exams",
    "exam_schedules",
    "assignments",
    "grade_sheets",
    "announcements",
    "student_attendance",
    "teacher_attendance",
    "questions",
    "question_forms",
    "class_fees",
    "financial_settings",
    "payment_receipts",
    "financial_statements",
    "groups",
    "chapters_management",
    "exam_types",
    "canteen_products",
    "canteen_transactions",
    "salary_management",
  ]),
  academic_manager: new Set([
    "topics",
    "exam_gate_schedules",
    "subject_grade_sheets",
    "class_group_for_teachers",
    "class_group_teachers",
    "class_subjects",
    "class_group_students",
    "class_groups",
    "teachers",
    "subjects",
    "academic_dashboard",
    "classes",
    "subjects",
    "exam_forms",
    "exam_schedules",
    "grade_sheets",
    "announcements",
    "teacher_attendance",
    "questions",
    "groups",
    "chapters_management",
  ]),
  student_affairs: new Set([
    "student_guardians",
    "student_affairs_dashboard",
    "classes",
    "students",
    "guardians",
    "announcements",
    "student_attendance",
    "class_groups",
    "class_group_students",
    "student_promotion",
    "groups",
    "posts",
    "promotion_history",
    "academic_years",
  ]),
  teacher: new Set([
    "assignments",
    "exams",
    "exam_schedules",
    "student_attendance",
    "questions",
    "question_forms",
    "grade_sheets",
  ]),
  finance: new Set([
    "class_fees",
    "financial_settings",
    "payment_receipts",
    "financial_statements",
    "salary_management",
    "salary_receipts",
    "salary_statements",
  ]),
  student: new Set([
    "class_schedules",
    "assignments",
    "student_attendance",
    "grade_sheets",
  ]),
  guardian: new Set(["student_attendance", "payment_receipts"]),
  canteen: new Set(["canteen_products", "canteen_transactions"]),
};

const ROLE_MODULE_ORDER = {
  school_admin: [
    "admin_dashboard",
    "users",
    "classes",
    "students",
    "teachers",
    "guardians",
    "subjects",
    "exams",
    "assignments",
    "grade_sheets",
    "announcements",
    "student_attendance",
    "teacher_attendance",
    "questions",
    "question_forms",
    "class_fees",
    "financial_settings",
    "payment_receipts",
    "financial_statements",
    "groups",
    "chapters_management",
    "exam_types",
    "canteen_products",
    
    "canteen_transactions",
  ],

  academic_manager: [
    "dashboard.view",
    "academic_dashboard",
    "classes",
    "teachers",
    "subjects",
    "exam_schedules",
    "exams",
    "grade_sheets",
    "announcements",
    "teacher_attendance",
    "questions_bank",
    "questions",
    "question_forms",
    "exam_forms",
    "groups",
    "chapters_management",
  ],

  student_affairs: [
    "dashboard.view",
    "student_affairs_dashboard",
    "classes",
    "students",
    "guardians",
    "announcements",
    "student_attendance",
  ],

  teacher: [
    "assignments",
    "exams",
    "student_attendance",
    "questions",
    "question_forms",
    "grade_sheets",
  ],

  finance: [
    "class_fees",
    "financial_settings",
    "financial_statements",
    "payment_receipts",
  ],

  student: [
    "class_schedules",
    "assignments",
    "student_attendance",
    "grade_sheets",
  ],

  guardian: ["student_attendance", "payment_receipts"],

  canteen: ["canteen", "canteen_products"],
};

function sortBySidebarOrder(modKeys, roleSlug) {
  const order = ROLE_MODULE_ORDER[roleSlug] || [];
  const idx = new Map(order.map((m, i) => [m, i]));
  return [...modKeys].sort((a, b) => {
    const ia = idx.has(a) ? idx.get(a) : Number.POSITIVE_INFINITY;
    const ib = idx.has(b) ? idx.get(b) : Number.POSITIVE_INFINITY;
    if (ia !== ib) return ia - ib;
    const la = tt(`sidebar.${a}`, a).toString().toLowerCase();
    const lb = tt(`sidebar.${b}`, b).toString().toLowerCase();
    return la.localeCompare(lb);
  });
}

const tt = (key, fallback) => {
  const v = t(key);
  return v === key ? fallback ?? key : v;
};

const ROLE_DENY = {
  student_affairs: new Set([
    "student_attendance.delete",
    "classes.delete",
    "classes.update",
    "classes.create",
    "class_groups.delete",
    "class_groups.update",
    "class_groups.create",
    "class_group_students.delete",
    "class_group_students.update",
    "class_group_students.create",
 
  ]),
  academic_manager: new Set([
    "class_subjects.delete",
    "class_subjects.create",
    "class_group_students.delete",
    "class_group_students.update",
    "class_group_students.create",
    "class_schedules.delete",
    "class_schedules.create",
    "class_groups.delete",
    "class_groups.update",
    "class_groups.create",
    "class_group_teachers.update",
    "exam_schedules.create",
    "teacher_attendance.delete",
    "exam_forms.update",
    "posts",
  ]),
  finance: new Set([
    "financial_settings.delete",
    "financial_settings.create",
    "financial_statements.delete",
    "financial_statements.update",
    "financial_statements.create",
  ]),
  canteen: new Set([
    "canteen_transactions.delete",
    "canteen_transactions.update",
    "canteen_transactions.create",
  ]),
};

const isDenied = (m, a) => {
  const slugStr = slug(m, a);
  const r = selectedRoleSlug.value;
  return r && ROLE_DENY[r]?.has(slugStr);
};

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      id: null,
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      gender: "male",
      role_id: null,
      phone_num: "",
      avatar_url: null,
      role: null,
      salary: 0,
      deserved_salary: 0,
    }),
    validator: (v) => {
      if (v.gender) v.gender = String(v.gender).toLowerCase();
      if (v.role && !v.role_id) v.role_id = v.role.id;
      return true;
    },
  },
  isEdit: { type: Boolean, default: false },
});

const emit = defineEmits([
  "submit",
  "cancel",
  "validation-error",
  "update:form-data",
]);

const roles = ref([]);
const formData = ref({ ...props.initialData });
const fileInput = ref(null);
const avatarPreview = ref(props.initialData?.avatar_url || null);
const avatarFile = ref(null);
const savingUser = ref(false);
const errors = ref({});

/* ----- Roles (translated & filtered) ----- */
const translatedRoles = computed(() => {
  const list = (roles.value || []).filter((r) => !HIDDEN_ROLE_SLUGS.has(r.slug));
  return list.map((role) => {
    try {
      const nameData =
        typeof role.name === "string" ? JSON.parse(role.name) : role.name;
      return {
        ...role,
        translated_name: nameData?.[locale.value] || nameData?.en || role.name,
      };
    } catch {
      return { ...role, translated_name: role.name };
    }
  });
});

const selectedRole = computed(
  () => translatedRoles.value.find((r) => r.id === formData.value.role_id) || null
);
const selectedRoleSlug = computed(() => selectedRole.value?.slug || null);

/* ----- Permissions state ----- */
const permsLoading = ref(false);
const allSlugs = ref([]); // all slugs from API
const excludedSlugs = ref(new Set()); // unchecked slugs

function parseSlug(s) {
  const parts = String(s).split(".");
  if (parts.length < 2) return { module: s, action: "" };
  const action = parts.pop();
  const module = parts.join(".");
  return { module, action };
}

const validSlugSet = computed(() => new Set(allSlugs.value));

/** modules present in API */
const modulesInApi = computed(() => {
  const s = new Set();
  allSlugs.value.forEach((sl) => {
    const { module, action } = parseSlug(sl);
    if (module && action) s.add(module);
  });
  return s;
});

/** Union of modules across all defined roles (minus globally hidden ones) */
const ALL_ROLE_MODULES = computed(() => {
  const u = new Set();
  Object.values(ROLE_SIDEBAR_MODULES).forEach((set) => {
    set.forEach((m) => u.add(m));
  });
  return u;
});

/** visible modules: admin = union; others = role's set; intersect with API */
const visibleModuleKeys = computed(() => {
  if (!selectedRoleSlug.value) return [];

  const baseSet =
    selectedRoleSlug.value === "school_admin"
      ? ALL_ROLE_MODULES.value
      : ROLE_SIDEBAR_MODULES[selectedRoleSlug.value] || new Set();

  const primary = [];
  baseSet.forEach((m) => {
    if (modulesInApi.value.has(m)) primary.push(m);
  });

  if (!primary.length && selectedRoleSlug.value === "canteen") {
    const inferred = Array.from(modulesInApi.value).filter((m) =>
      m.startsWith("canteen_")
    );
    return sortBySidebarOrder(inferred, selectedRoleSlug.value);
  }

  return sortBySidebarOrder(primary, selectedRoleSlug.value);
});

/** optional search */
const search = ref("");
const filteredModuleKeys = computed(() => {
  const base = visibleModuleKeys.value;
  if (!search.value) return base;
  const needle = search.value.toLowerCase();
  return base.filter((m) =>
    tt(`sidebar.${m}`, m).toString().toLowerCase().includes(needle)
  );
});

/** actions per module */
const actionsByModule = computed(() => {
  const map = new Map();
  allSlugs.value.forEach((s) => {
    const { module, action } = parseSlug(s);
    if (!module || !action) return;
    if (!map.has(module)) map.set(module, new Set());
    map.get(module).add(action);
  });
  return map;
});

/** set of visible slugs for bulk ops */
const visibleSlugSet = computed(() => {
  const set = new Set();
  visibleModuleKeys.value.forEach((m) => {
    const acts = actionsByModule.value.get(m) || new Set();
    acts.forEach((a) => set.add(`${m}.${a}`));
  });
  return set;
});

/** accordion open state */
const openModules = ref(new Set());
const allOpen = computed(() =>
  filteredModuleKeys.value.every((m) => openModules.value.has(m))
);
const toggleModule = (m) => {
  const set = new Set(openModules.value);
  set.has(m) ? set.delete(m) : set.add(m);
  openModules.value = set;
};
const toggleAllOpen = () => {
  if (allOpen.value) openModules.value = new Set();
  else openModules.value = new Set(filteredModuleKeys.value);
};

/* Helpers */
const slug = (m, a) => `${m}.${a}`;
const actionExists = (m, a) => validSlugSet.value.has(slug(m, a));
const isAllowed = (m, a) => !excludedSlugs.value.has(slug(m, a));
const badgeClass = (ok, exists = true) => {
  if (!exists) return "badge bg-secondary-subtle text-muted border";
  return ok
    ? "badge bg-success-subtle text-success border"
    : "badge bg-danger-subtle text-danger border";
};
const badgeText = (ok, exists = true) => {
  if (!exists) return tt("permissions.not_available", "N/A");
  return ok ? tt("permissions.allowed", "نعم") : tt("permissions.denied", "لا");
};

/* Row helpers */
const rowAllChecked = (m) => {
  const acts = actionsByModule.value.get(m) || new Set();
  if (!acts.size) return false;
  for (const a of acts) {
    const s = slug(m, a);
    if (excludedSlugs.value.has(s)) return false;
  }
  return true;
};
const onToggleRow = (m, checked) => {
  const acts = actionsByModule.value.get(m) || new Set();
  const next = new Set(excludedSlugs.value);
  acts.forEach((a) => {
    if (isDenied(m, a)) return; // تجاهل الممنوع
    const s = slug(m, a);
    if (checked) next.delete(s);
    else next.add(s);
  });
  excludedSlugs.value = next;
};
const onToggle = (s, checked) => {
  if (!validSlugSet.value.has(s)) return;
  const next = new Set(excludedSlugs.value);
  if (checked) next.delete(s);
  else next.add(s);
  excludedSlugs.value = next;
};

/* Global allow/deny — ONLY affect visible slugs */
const allowAllVisible = () => {
  const next = new Set(excludedSlugs.value);
  visibleSlugSet.value.forEach((s) => {
    const parts = s.split(".");
    const m = parts.slice(0, -1).join(".");
    const a = parts[parts.length - 1];
    if (!isDenied(m, a)) next.delete(s);
  });
  excludedSlugs.value = next;
};
const denyAllVisible = () => {
  const next = new Set(excludedSlugs.value);
  visibleSlugSet.value.forEach((s) => next.add(s));
  excludedSlugs.value = next;
};

/* Counters */
const selectedCountInModule = (m) => {
  const acts = actionsByModule.value.get(m) || new Set();
  let sel = 0;
  acts.forEach((a) => {
    if (!excludedSlugs.value.has(slug(m, a))) sel++;
  });
  return sel;
};
const excludedSlgsHas = (s) => excludedSlugs.value.has(s);
const selectedSlugs = computed(() =>
  allSlugs.value.filter((s) => !excludedSlgsHas(s))
);

/* API */
async function loadRolePermissions(roleId) {
  permsLoading.value = true;
  try {
    const { data } = await api.get(`/roles/${roleId}/permissions`);
    const all = (data?.all ?? []).map((p) => String(p.slug));
    const granted = Array.isArray(data?.granted) ? data.granted.map(String) : [];
    allSlugs.value = all;
    excludedSlugs.value = new Set(all.filter((s) => !granted.includes(s)));
    openModules.value = new Set((filteredModuleKeys.value || []).slice(0, 6));
  } finally {
    permsLoading.value = false;
  }
}

async function loadUserPermissions(userId) {
  permsLoading.value = true;
  try {
    const { data } = await api.get(`/users/${userId}/permissions`);
    const all = (data?.all ?? []).map((p) => String(p.slug));
    const granted = Array.isArray(data?.granted) ? data.granted.map(String) : [];

    allSlugs.value = all;
    excludedSlugs.value = new Set(all.filter((s) => !granted.includes(s)));

    didLoadUserPerms.value = true; // ✅ important for edit
    openModules.value = new Set((filteredModuleKeys.value || []).slice(0, 6));
  } finally {
    permsLoading.value = false;
  }
}

/* Avatar */
function handleImageError(e) {
  e.target.src = "/images/default-avatar.jpg";
}
function handleFileChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  if (!file.type.match("image.*")) {
    toast.error("الرجاء اختيار ملف صورة");
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    toast.error("حجم الصورة يجب أن يكون أقل من 2MB");
    return;
  }
  const reader = new FileReader();
  reader.onload = (ev) => {
    avatarPreview.value = ev.target.result;
  };
  reader.readAsDataURL(file);
  avatarFile.value = file;
  emit("update:form-data", formData.value);
}
function removeAvatar() {
  avatarPreview.value = null;
  avatarFile.value = null;
  formData.value.avatar_url = null;
  if (fileInput.value) fileInput.value.value = "";
  emit("update:form-data", formData.value);
}

/* Validation + submit */
function validateForm() {
  errors.value = {};
  let ok = true;

  if (!formData.value.name?.trim()) {
    errors.value.name = ["حقل الاسم مطلوب"];
    ok = false;
  }
  if (!formData.value.email?.trim()) {
    errors.value.email = ["حقل البريد الإلكتروني مطلوب"];
    ok = false;
  } else if (!/^\S+@\S+\.\S+$/.test(formData.value.email)) {
    errors.value.email = ["البريد الإلكتروني غير صالح"];
    ok = false;
  }
  if (!formData.value.role_id) {
    errors.value.role_id = ["حقل الدور الوظيفي مطلوب"];
    ok = false;
  }

  if (!props.isEdit) {
    if (!formData.value.password) {
      errors.value.password = ["حقل كلمة المرور مطلوب"];
      ok = false;
    } else if (formData.value.password.length < 8) {
      errors.value.password = ["كلمة المرور يجب أن تكون 8 أحرف على الأقل"];
      ok = false;
    }
    if (formData.value.password !== formData.value.password_confirmation) {
      errors.value.password_confirmation = ["كلمة المرور غير متطابقة"];
      ok = false;
    }
  }

  if (formData.value.salary != null && Number(formData.value.salary) < 0) {
    errors.value.salary = ["الراتب يجب أن يكون 0 أو أكثر"];
    ok = false;
  }
  if (
    formData.value.deserved_salary != null &&
    Number(formData.value.deserved_salary) < 0
  ) {
    errors.value.deserved_salary = ["المستحق يجب أن يكون 0 أو أكثر"];
    ok = false;
  }

  if (!ok) emit("validation-error");
  return ok;
}

async function handleSubmit() {
  if (!validateForm()) return;
  savingUser.value = true;
  try {
    const payload = isSchoolAdmin.value
      ? { ...formData.value }
      : { ...formData.value, permission_slugs: selectedSlugs.value };

    if (avatarFile.value) {
      const fd = new FormData();

      Object.keys(payload).forEach((k) => {
        if (
          props.isEdit &&
          (k === "password" || k === "password_confirmation") &&
          !payload[k]
        )
          return;
        if (k === "permission_slugs") return;
        fd.append(k, payload[k] ?? "");
      });

      fd.append("avatar", avatarFile.value);

      if (props.isEdit) {
        fd.append("_method", "PUT");
      }

      if (!isSchoolAdmin.value) {
        selectedSlugs.value.forEach((s) => fd.append("permission_slugs[]", s));
      }

      emit("submit", fd);
    } else {
      if (props.isEdit) {
        if (!payload.password) delete payload.password;
        if (!payload.password_confirmation) delete payload.password_confirmation;
        
      }

      if (isSchoolAdmin.value) delete payload.permission_slugs;

      emit("submit", payload);
    }
  } finally {
    savingUser.value = false;
  }
}
function setServerErrors(serverErrors) {
  errors.value = serverErrors || {};
}
defineExpose({ submit: handleSubmit, setServerErrors });

watch(
  () => formData.value.role_id,
  (newId, oldId) => {
    if (suppressPermsReload.value) return;
    if (props.isEdit && oldId && newId && newId !== oldId) {
      userChangedRole.value = true;
    }
  }
);

/* Watchers */
watch(
  () => props.initialData,
  async (v) => {
    userChangedRole.value = false;
    roleIdInitialized.value = false;

    suppressPermsReload.value = true;
    didLoadUserPerms.value = false;

    formData.value = { ...v };
    if (v?.avatar_url) avatarPreview.value = v.avatar_url;

    if (props.isEdit && v?.id) {
      await loadUserPermissions(v.id);
    }

    suppressPermsReload.value = false;
  },
  { deep: true }
);

watch(
  [formData, selectedSlugs],
  ([vf, vs]) => {
    emit("update:form-data", { ...vf, permission_slugs: vs });
  },
  { deep: true }
);

watch(
  selectedRole,
  async (r) => {
    if (suppressPermsReload.value) return;

    if (props.isEdit && didLoadUserPerms.value && !userChangedRole.value) {
      return;
    }

    excludedSlugs.value = new Set();
    allSlugs.value = [];

    if (r?.id && !isSchoolAdmin.value) {
      await loadRolePermissions(r.id);
    } else {
      openModules.value = new Set();
    }
  },
  { immediate: false }
);

/* Mount */
onMounted(async () => {
  try {
    let list = [];
    try {
      const { data } = await api.get("/roles?assignable=1");
      list = Array.isArray(data) ? data : [];
    } catch {
      if (!store.getters.getRoles?.length) {
        await store.dispatch("fetchRoles");
      }
      list = store.getters.getRoles || [];
    }
    roles.value = list;

    if (formData.value.role_id) {
      const selected = roles.value.find((r) => r.id === formData.value.role_id);
      if (selected && HIDDEN_ROLE_SLUGS.has(selected.slug)) {
        formData.value.role_id = null;
      }
    }

    suppressPermsReload.value = true;

    if (props.isEdit && props.initialData?.id) {
      await loadUserPermissions(props.initialData.id);
    } else if (formData.value.role_id) {
      await loadRolePermissions(formData.value.role_id);
    }

    suppressPermsReload.value = false;
  } catch (e) {
    console.error("Failed to fetch roles/permissions:", e);
    toast.error("فشل في تحميل الأدوار/الصلاحيات");
    suppressPermsReload.value = false;
  }
});

</script>

<style scoped>
.avatar-container:hover {
  border-color: #007bff !important;
}
.cursor-pointer {
  cursor: pointer;
}

.borderless-input {
  border: none;
  border-bottom: 1px solid #ced4da;
  border-radius: 0;
  padding-left: 0;
}
.borderless-input:focus {
  box-shadow: none;
  border-bottom-color: #007bff;
}

.form-select-rtl {
  text-align: right;
  direction: rtl;
}
.form-select-ltr {
  text-align: left;
  direction: ltr;
}

.perms-search {
  min-width: 240px;
}

.accordion-item {
  background: #fff;
  border: 1px solid #e9ecef !important;
  border-radius: 0.5rem !important;
}
.accordion-button {
  font-weight: 600;
  background: #f8f9fa;
  border-radius: 0.5rem !important;
}
.accordion-button:focus {
  box-shadow: none;
}
.accordion-button:not(.collapsed) {
  background: #eef3ff;
}

.bg-success-subtle {
  background-color: #d1e7dd !important;
}
.bg-danger-subtle {
  background-color: #f8d7da !important;
}
.bg-secondary-subtle {
  background-color: #e2e3e5 !important;
}
.badge {
  font-weight: 600;
}

@media (max-width: 767.98px) {
  :host,
  :root,
  .col-12,
  .row,
  form {
    max-width: 100%;
    overflow-x: hidden;
  }
}
[dir="rtl"] .accordion-button::after {
  margin-left: 0;
  margin-right: auto;
  transform: rotate(180deg);
}
.perms-head-btn::after {
  display: none;
}

.perms-head-btn .mod-left {
  flex: 1 1 auto;
  min-width: 0;
}
.perms-head-btn .mod-right {
  flex: 0 0 auto;
  margin-inline-start: auto;
  gap: 0.75rem;
}

.perms-head-btn .accordion-caret {
  font-size: 0.9rem;
}

[dir="rtl"] .perms-head-btn .mod-left i {
  margin-left: 0;
  margin-right: 0;
}
</style>
